/**
 * The app-wide element filter (APP_SPEC "Filtering is first-class"):
 * the rules engine's own RuleSelector vocabulary promoted to URL
 * search params. The URL is the one channel every module reads
 * without importing anyone (law 3) -- the shell's project route
 * validates with parseFilterSearch, modules read via useSearch.
 *
 * `category` is app-side plural (an include list; omitted = all
 * kinds) -- a single value is exactly a RuleSelector.category, so a
 * one-category filter copies into a rule scope verbatim.
 */

export interface ElementFilter {
  category?: string[];
  ifc_class?: string;
  object_type?: string;
  name_regex?: string;
  // The spatial axis (APP_SPEC "Drilling"): the height_band
  // evaluator's own params promoted to the URL beside the selector.
  // An element matches when its z-extent OVERLAPS the band.
  min_z_meters?: number;
  max_z_meters?: number;
}

const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v.length > 0 ? v : undefined;

const num = (v: unknown): number | undefined => {
  const parsed = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(parsed) ? parsed : undefined;
};

/** TanStack Router validateSearch: unknown search -> typed filter.
 * category arrives as the router's JSON array (its own serialization
 * of what validateSearch returned) or as a hand-typed CSV string --
 * both parse; garbage params drop silently. */
export function parseFilterSearch(search: Record<string, unknown>): ElementFilter {
  const filter: ElementFilter = {};
  const category = Array.isArray(search.category)
    ? search.category.filter((v): v is string => typeof v === 'string' && v.length > 0)
    : str(search.category)?.split(',').filter(Boolean);
  if (category?.length) filter.category = category;
  const ifcClass = str(search.ifc_class);
  if (ifcClass) filter.ifc_class = ifcClass;
  const objectType = str(search.object_type);
  if (objectType) filter.object_type = objectType;
  const nameRegex = str(search.name_regex);
  if (nameRegex) filter.name_regex = nameRegex;
  const minZ = num(search.min_z_meters);
  if (minZ !== undefined) filter.min_z_meters = minZ;
  const maxZ = num(search.max_z_meters);
  if (maxZ !== undefined) filter.max_z_meters = maxZ;
  return filter;
}

/** The filter back into search-param form (undefined clears a param
 * -- TanStack drops undefined keys from the URL and JSON-serializes
 * the array itself). */
export function filterToSearch(
  filter: ElementFilter,
): Record<string, string | string[] | number | undefined> {
  return {
    category: filter.category?.length ? filter.category : undefined,
    ifc_class: filter.ifc_class,
    object_type: filter.object_type,
    name_regex: filter.name_regex,
    min_z_meters: filter.min_z_meters,
    max_z_meters: filter.max_z_meters,
  };
}

export function filterIsActive(filter: ElementFilter): boolean {
  return Boolean(
    filter.category?.length ||
      filter.ifc_class ||
      filter.object_type ||
      filter.name_regex ||
      filter.min_z_meters !== undefined ||
      filter.max_z_meters !== undefined,
  );
}

/** An element geometry's [zmin, zmax] over every coordinate, or null
 * when no z can be read (such an element ignores the band -- the band
 * is a spatial verb, and geometry-less rows have no place in it). */
export function zExtent(geometry: unknown): [number, number] | null {
  let min = Infinity;
  let max = -Infinity;
  const walk = (node: unknown): void => {
    if (!Array.isArray(node)) return;
    if (node.length >= 3 && node.every((v) => typeof v === 'number')) {
      const z = node[2] as number;
      if (z < min) min = z;
      if (z > max) max = z;
      return;
    }
    for (const child of node) walk(child);
  };
  walk((geometry as { coordinates?: unknown } | null)?.coordinates);
  return min <= max ? [min, max] : null;
}

/** One matching law for every screen. Fields the element payload
 * does not carry (raw_attributes absent) fail closed for that chip:
 * an active filter never silently widens. */
export function elementMatchesFilter(
  el: {
    category: string | null;
    raw_attributes?: Record<string, unknown>;
    geometry?: unknown;
  },
  filter: ElementFilter,
): boolean {
  if (filter.min_z_meters !== undefined || filter.max_z_meters !== undefined) {
    const extent = zExtent(el.geometry);
    if (!extent) return false;
    if (filter.max_z_meters !== undefined && extent[0] > filter.max_z_meters) return false;
    if (filter.min_z_meters !== undefined && extent[1] < filter.min_z_meters) return false;
  }
  if (filter.category?.length && !filter.category.includes(el.category ?? 'OTHER')) {
    return false;
  }
  if (filter.ifc_class && el.raw_attributes?.ifc_class !== filter.ifc_class) return false;
  if (filter.object_type && el.raw_attributes?.object_type !== filter.object_type) {
    return false;
  }
  if (filter.name_regex) {
    const name = el.raw_attributes?.element_name;
    if (typeof name !== 'string') return false;
    try {
      if (!new RegExp(filter.name_regex).test(name)) return false;
    } catch {
      return false; // an unparseable regex matches nothing, loudly-by-emptiness
    }
  }
  return true;
}
